import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { FaSearch, FaBriefcase, FaBuilding, FaMapMarkerAlt, FaCheckCircle, FaArrowRight, FaArrowUp, FaFilter, FaClock, FaGlobe, FaMoneyBillWave, FaShareAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';
import Loader from '../shared/Loader';
import { API_URL } from '../../config';
import DualRangeSlider from '../shared/DualRangeSlider';
import ShareModal from '../shared/ShareModal';
import './BrowsePage.css';
import { COURSE_CATEGORIES } from '../../constants';

const BrowseJobs = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { search: urlSearch } = useLocation();

    const [jobs, setJobs] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    // Process URL params
    const queryParams = new URLSearchParams(urlSearch);
    const initialSearch = queryParams.get('q') || '';
    const initialType = queryParams.get('type') || '';
    const initialCategory = queryParams.get('category') || '';

    const [search, setSearch] = useState(initialSearch);
    const [filterEmploymentType, setFilterEmploymentType] = useState(initialType);
    const [filterCategory, setFilterCategory] = useState(initialCategory);
    const [filterLocation, setFilterLocation] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [filterPostedDate, setFilterPostedDate] = useState('all'); // all, 24h, 7d, 30d
    const [jobTypeTab, setJobTypeTab] = useState('private'); // private, govt
    const [locations, setLocations] = useState([]);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [industries, setIndustries] = useState([]); // New state for industries
    const [uniqueCompanies, setUniqueCompanies] = useState([]); // New state for companies

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const [minExp, setMinExp] = useState(0);
    const [maxExp, setMaxExp] = useState(60);
    const [minSalary, setMinSalary] = useState(0);
    const [maxSalary, setMaxSalary] = useState(2000000);
    const [selectedIndustries, setSelectedIndustries] = useState([]); // Will keep for logic but hide UI as requested
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareData, setShareData] = useState({ url: '', title: '' });

    // Scroll listener for Scroll-to-Top button
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 400) setShowScrollTop(true);
            else setShowScrollTop(false);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        axios.get('/api/public/jobs?limit=100')
            .then(r => {
                setJobs(r.data);
                setFiltered(r.data);
                const locs = [...new Set(r.data.map(j => j.location).filter(Boolean))];
                setLocations(locs);
                const cats = [...new Set(r.data.map(j => j.category).filter(Boolean))];
                setCategories(cats);
                const inds = [...new Set(r.data.map(j => j.industry).filter(Boolean))];
                setIndustries(inds.sort());
                const comps = [...new Set(r.data.map(j => j.companyId?.name || j.company).filter(Boolean))];
                setUniqueCompanies(comps.sort());
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const standardizedCategories = COURSE_CATEGORIES;

    // Update categories dynamically — SHOW ONLY CATEGORIES WITH AVAILABLE JOBS
    useEffect(() => {
        if (jobs.length > 0) {
            const relevantJobs = jobs.filter(j => (j.jobType || 'private').toLowerCase() === jobTypeTab.toLowerCase());
            const availableCategories = [...new Set(relevantJobs.map(j => j.category).filter(Boolean))];
            
            // Sort them based on our global order for consistency, or just alphabetically
            const combined = availableCategories.sort((a, b) => a.localeCompare(b));
            setCategories(combined);
        } else {
            setCategories([]);
        }
    }, [jobs, jobTypeTab]);

    useEffect(() => {
        let result = jobs;

        // Search filter (Title or Company)
        if (search) {
            result = result.filter(j =>
                j.title.toLowerCase().includes(search.toLowerCase()) ||
                (j.companyId?.name || j.company || '').toLowerCase().includes(search.toLowerCase())
            );
        }

        // Sidebar Filters
        if (jobTypeTab) {
            result = result.filter(j => (j.jobType || 'private').toLowerCase() === jobTypeTab.toLowerCase());
        }
        if (filterEmploymentType) {
            result = result.filter(j =>
                (j.type || '').toLowerCase().replace(/[^a-z]/g, '') === filterEmploymentType.toLowerCase().replace(/[^a-z]/g, '')
            );
        }
        if (filterCategory) {
            result = result.filter(j => (j.category || '').toLowerCase() === filterCategory.toLowerCase());
        }
        if (filterLocation) {
            result = result.filter(j => (j.location || '').toLowerCase().includes(filterLocation.toLowerCase()));
        }
        if (filterCompany) {
            result = result.filter(j => (j.companyId?.name || j.company || '').toLowerCase().includes(filterCompany.toLowerCase()));
        }

        // Helper to extract numeric value from strings like "5 years", "$60,000", "60k-80k"
        const parseNum = (val) => {
            if (typeof val === 'number') return val;
            if (!val) return 0;
            // Clean string: remove commas, lowercase it
            let s = val.toString().toLowerCase().replace(/,/g, '');
            
            let multiplier = 1;
            if (s.includes('k')) multiplier = 1000;
            else if (s.includes('m')) multiplier = 1000000;
            
            // Extract the first number (including decimals) found in the string
            const match = s.match(/[\d.]+/);
            if (!match) return 0;
            
            const num = parseFloat(match[0]);
            return isNaN(num) ? 0 : num * multiplier;
        };

        // Experience Filter (Robust parsing)
        result = result.filter(j => {
            const exp = parseNum(j.experience);
            return exp >= parseNum(minExp) && exp <= parseNum(maxExp);
        });

        // Salary Filter (Robust parsing)
        result = result.filter(j => {
            const sal = parseNum(j.salaryRange || j.salary);
            return sal >= parseNum(minSalary) && sal <= parseNum(maxSalary);
        });

        // Industry Filter
        if (selectedIndustries.length > 0) {
            result = result.filter(j => selectedIndustries.includes(j.industry));
        }

        setFiltered(result);
        setCurrentPage(1); // Reset to first page on filter change
    }, [search, filterEmploymentType, filterCategory, filterLocation, filterCompany, minExp, maxExp, minSalary, maxSalary, jobTypeTab, jobs, selectedIndustries]);

    // Paginated Data
    const paginatedItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const totalPages = Math.ceil(filtered.length / pageSize);

    // Handle Suggestions
    useEffect(() => {
        if (!search || search.length < 2) {
            setSuggestions([]);
            return;
        }
        const lower = search.toLowerCase();
        const matches = jobs
            .filter(j => j.title.toLowerCase().includes(lower))
            .slice(0, 5)
            .map(j => ({ type: 'job', text: j.title, id: j._id }));
        setSuggestions(matches);
    }, [search, jobs]);

    const handleReset = () => {
        setSearch('');
        setFilterEmploymentType('');
        setFilterCategory('');
        setFilterLocation('');
        setFilterCompany('');
        setFilterPostedDate('all');
        setMinExp(0);
        setMaxExp(60);
        setSelectedIndustries([]);
        setMinSalary(0);
        setMaxSalary(2000000);
        navigate('/browse/jobs', { replace: true });
    };

    const handleIndustryToggle = (industry) => {
        setSelectedIndustries(prev =>
            prev.includes(industry)
                ? prev.filter(i => i !== industry)
                : [...prev, industry]
        );
    };

    const handleApply = (jobId) => {
        if (!user) {
            navigate('/auth');
            return;
        }
        // Allow both job_seeker and student to apply
        if (user.role === 'job_seeker' || user.role === 'student') {
            const path = user.role === 'job_seeker' ? '/jobseeker-dashboard' : '/student-dashboard';
            navigate(path);
            return;
        }
        // Any other role — tell them to create the right account
        Swal.fire({
            icon: 'warning',
            title: 'Candidate Account Required',
            html: `You're currently signed in as <strong>${user.role.replace('_', ' ')}</strong>.<br/>To apply for jobs, you need to be a <strong>Job Seeker</strong> or a <strong>Student</strong>.<br/><br/>Please register as a candidate to continue.`,
            confirmButtonText: 'Create Account',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1a3a6b'
        }).then(r => { if (r.isConfirmed) navigate('/auth?type=job_seeker'); });
    };

    const handleShare = (e, job) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/public-job/${job._id}`;
        setShareData({ url: shareUrl, title: job.title });
        setIsShareModalOpen(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) return <Loader fullScreen={true} />;

    return (
        <div className="browse-root">
            {/* HERO V2 */}
            <div className="browse-hero-v2">
                <Link to="/" className="browse-back-btn">
                    <ArrowLeft size={18} />
                    <span>Back</span>
                </Link>
                <div className="browse-hero-skyline"></div>
                <h1>Find Available Job Positions</h1>

                <div className="browse-pill-search-wrap glass-wrap">
                    <div className="browse-pill-search">
                        <div className="search-icon-box">
                            <FaSearch />
                        </div>
                        <input
                            type="text"
                            placeholder="Job Title or keyword"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => setShowSuggestions(true)}
                        />
                        <button className="search-prime-btn">Search</button>
                    </div>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="pp-search-suggestions" style={{ top: '100%', left: '20px', right: '20px' }}>
                            {suggestions.map((s, idx) => (
                                <div
                                    key={idx}
                                    className="pp-suggestion-item"
                                    onClick={() => {
                                        setSearch(s.text);
                                        setShowSuggestions(false);
                                    }}
                                >
                                    <FaSearch className="pp-suggest-icon" />
                                    <span>{s.text}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

            <div className="browse-layout-container">
                {/* SIDEBAR */}
                <aside className={`browse-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                    {/* Job Type Toggle */}
                    <div className="filter-toggle-group" style={{ marginBottom: '24px' }}>
                        <button
                            className={`filter-toggle-btn ${jobTypeTab === 'private' ? 'active' : ''}`}
                            onClick={() => setJobTypeTab('private')}
                        >
                            <FaBriefcase /> Private
                        </button>
                        <button
                            className={`filter-toggle-btn ${jobTypeTab === 'govt' ? 'active' : ''}`}
                            onClick={() => setJobTypeTab('govt')}
                        >
                            <FaBuilding /> Govt.
                        </button>
                    </div>

                    <div className="filter-card">
                        <div className="filter-header">
                            <FaFilter /> Filter
                        </div>

                        <div className="filter-group">
                            <label>Employment Type</label>
                            <select
                                className="filter-input"
                                value={filterEmploymentType}
                                onChange={(e) => setFilterEmploymentType(e.target.value)}
                            >
                                <option value="">All Types</option>
                                <option value="Full Time">Full Time</option>
                                <option value="Part Time">Part Time</option>
                                <option value="Remote">Remote</option>
                                <option value="Contract">Contract</option>
                                <option value="Internship">Internship</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Job Category</label>
                            <select
                                className="filter-input"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Company</label>
                            <select
                                className="filter-input"
                                value={filterCompany}
                                onChange={(e) => setFilterCompany(e.target.value)}
                            >
                                <option value="">All Companies</option>
                                {uniqueCompanies.map(comp => (
                                    <option key={comp} value={comp}>{comp}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Location</label>
                            <input
                                type="text"
                                className="filter-input"
                                placeholder="Enter desired location"
                                value={filterLocation}
                                onChange={(e) => setFilterLocation(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>Experience Level (Year)</label>
                            <div className="filter-range-wrap">
                                <DualRangeSlider
                                    min={0}
                                    max={60}
                                    minValue={minExp}
                                    maxValue={maxExp}
                                    onChange={({ min, max }) => {
                                        setMinExp(min);
                                        setMaxExp(max);
                                    }}
                                />
                                <div className="range-inputs">
                                    <div className="range-box">
                                        <span>Min : ({minExp})</span>
                                        <input type="number" placeholder="0" value={minExp} onChange={(e) => setMinExp(parseInt(e.target.value) || 0)} />
                                    </div>
                                    <div className="range-box">
                                        <span>Max : ({maxExp})</span>
                                        <input type="number" placeholder="60" value={maxExp} onChange={(e) => setMaxExp(parseInt(e.target.value) || 0)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>Salary Range (Monthly)</label>
                            <div className="filter-range-wrap">
                                <DualRangeSlider
                                    min={0}
                                    max={2000000}
                                    step={1000}
                                    minValue={minSalary}
                                    maxValue={maxSalary}
                                    onChange={({ min, max }) => {
                                        setMinSalary(min);
                                        setMaxSalary(max);
                                    }}
                                />
                                <div className="range-inputs">
                                    <div className="range-box">
                                        <span>Min : ({minSalary})</span>
                                        <input type="number" placeholder="0" value={minSalary} onChange={(e) => setMinSalary(parseInt(e.target.value) || 0)} />
                                    </div>
                                    <div className="range-box">
                                        <span>Max : ({maxSalary})</span>
                                        <input type="number" placeholder="2000000" value={maxSalary} onChange={(e) => setMaxSalary(parseInt(e.target.value) || 0)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button className="browse-reset-btn" onClick={handleReset}>
                            Reset All Filters
                        </button>
                    </div>
                </aside>

                {/* MAIN CONTENT */}
                <main className="browse-main-content">
                    <div className="results-info-bar">
                        <div className="results-header-left">
                            <div className="results-count">
                                {filtered.length} Job{filtered.length !== 1 ? 's' : ''} Found
                            </div>
                        </div>
                        <div className="results-header-right">
                            <div className="entries-select-wrap">
                                <span>Show:</span>
                                <select
                                    className="entries-select"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                            <button className="mobile-filter-trigger" onClick={() => setIsSidebarOpen(true)}>
                                <FaFilter />
                            </button>
                        </div>
                    </div>

                    <div className="browse-results-list">
                        {paginatedItems.length === 0 ? (
                            <div className="pp-empty-premium">
                                <div className="pp-ep-icon-wrap"><FaBriefcase /></div>
                                <h3>No Jobs Match Your Search</h3>
                                <p>Try adjusting your filters or search term.</p>
                                <div className="pp-ep-glow"></div>
                            </div>
                        ) : (
                            filtered.map(job => (
                                <div
                                    key={job._id}
                                    className="browse-result-card"
                                    onClick={() => navigate(`/public-job/${job._id}`)}
                                >
                                    <div className="card-logo-box">
                                        {job.companyId?.logoUrl ? (
                                            <img
                                                src={job.companyId.logoUrl.startsWith('http') ? job.companyId.logoUrl : `${API_URL}${job.companyId.logoUrl.startsWith('/') ? '' : '/'}${job.companyId.logoUrl}`}
                                                alt="Logo"
                                            />
                                        ) : (
                                            <FaBriefcase className="card-logo-ph" />
                                        )}
                                    </div>

                                    <div className="card-main-info">
                                        <div className="card-top-row">
                                            <h3 className="card-title">{job.title}</h3>
                                            <div className="card-share-btn" onClick={(e) => handleShare(e, job)}>
                                                <FaShareAlt />
                                            </div>
                                        </div>
                                        <div className="card-company">
                                            {job.companyId?.name || job.company} {job.companyId?.isVerified && <FaCheckCircle style={{ fontSize: '12px' }} />}
                                        </div>

                                        <div className="card-meta-list">
                                            {job.type && (
                                                <div className={`card-type-badge ${job.type?.toLowerCase().replace(/\s+/g, '-')}`}>
                                                    <FaClock /> {job.type}
                                                </div>
                                            )}
                                            {job.location && <div className="card-meta-item"><FaMapMarkerAlt /> {job.location}</div>}
                                            {job.salaryRange && <div className="card-meta-item"><FaMoneyBillWave /> {job.salaryRange}</div>}
                                        </div>

                                        <div className="card-tags">
                                            {job.category && <span className="card-tag">{job.category}</span>}
                                            {job.skills && job.skills.slice(0, 3).map((s, i) => (
                                                <span key={i} className="card-tag">{s}</span>
                                            ))}
                                        </div>

                                        <div className="card-footer">
                                            {job.deadline && (
                                                <div className="card-deadline">
                                                    <FaClock /> Deadline {new Date(job.deadline).toLocaleDateString()}
                                                </div>
                                            )}
                                            <button
                                                className="card-apply-btn"
                                                onClick={(e) => { e.stopPropagation(); handleApply(job._id); }}
                                            >
                                                Apply Now
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="browse-pagination">
                            <button
                                className="pg-btn"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <FaChevronLeft />
                            </button>

                            {[...Array(totalPages)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`pg-num ${currentPage === i + 1 ? 'active' : ''}`}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </div>
                            ))}

                            <button
                                className="pg-btn"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </main>
            </div>

            {/* ===== SCROLL TO TOP ===== */}
            <button
                className={`pp-scroll-top ${showScrollTop ? 'visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Scroll to top"
            >
                <FaArrowUp />
            </button>

            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                shareUrl={shareData.url}
                title={shareData.title}
            />
        </div>
    );
};

export default BrowseJobs;
